// Aucun secret dans ce fichier. Le serveur relit les prix via l'API Shopify.
export function cartPayload(cart, country) {
  if (!cart?.items?.length) throw new Error('EMPTY');
  if (cart.note?.trim() || Object.values(cart.attributes || {}).some(Boolean) ||
      cart.total_discount > 0 || cart.cart_level_discount_applications?.length ||
      cart.items.some(item => item.selling_plan_allocation || item.parent_relationship ||
        Object.values(item.properties || {}).some(Boolean) || item.line_level_discount_allocations?.length)) {
    throw new Error('UNSUPPORTED');
  }
  const items = cart.items.map(item => ({ variantId: String(item.variant_id), quantity: item.quantity }));
  if (items.some(item => !/^[1-9]\d{0,19}$/.test(item.variantId) || !Number.isInteger(item.quantity) || item.quantity < 1) ||
      items.reduce((n, item) => n + item.quantity, 0) > 20 ||
      !Number.isSafeInteger(cart.total_price) || cart.total_price < 1 || !/^[A-Z]{3}$/.test(cart.currency)) throw new Error('UNSUPPORTED');
  if (country !== undefined && !/^[A-Z]{2}$/.test(country)) throw new Error('UNSUPPORTED');
  return { items, totalCents: cart.total_price, currency: cart.currency, ...(country ? { country } : {}) };
}

export function cartFingerprint(cart, country) {
  return JSON.stringify({ ...(country ? { country } : {}), currency: cart.currency, total: cart.total_price, token: cart.token,
    note: cart.note || '', attributes: cart.attributes || {},
    items: cart.items.map(item => [item.key, item.variant_id, item.quantity, item.final_line_price, item.properties || {}]) });
}

export function checkoutUrl(value) {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.hostname !== 'whop.com' || url.port || url.username || url.password ||
      !url.pathname.startsWith('/checkout/')) throw new Error('UNAVAILABLE');
  return url.href;
}

export function matchingAttempt(saved, fingerprint) {
  return saved && saved.fingerprint === fingerprint &&
    /^[A-Za-z0-9_-]{16,100}$/.test(saved.requestId) ? saved : null;
}

function install(config) {
  if (window.__botaleoWhopInstalled) return;
  window.__botaleoWhopInstalled = true;
  // Relire le réglage permet aussi de changer de destination dans l'éditeur du thème.
  // Les retours d'un paiement Whop déjà commencé restent traités en mode Shopify.
  const whopEnabled = () => {
    try {
      return JSON.parse(document.getElementById('botaleo-whop-config')?.textContent || '{}').enabled === true;
    } catch { return false; }
  };
  const root = window.Shopify?.routes?.root || '/';
  const endpoint = `${root}${config.proxyPath.replace(/^\//, '')}`;
  const selector = 'button[name="checkout"], input[name="checkout"], .cart__checkout-button';
  // Le style reste dans cet asset pour permettre une mise à jour en un seul fichier.
  const loadingStyle = document.createElement('style');
  loadingStyle.textContent = `
    .bw-checkout-pending :is(button[name="checkout"], button.cart__checkout-button) {
      position: relative !important; cursor: progress !important; opacity: 1 !important;
    }
    .bw-checkout-pending :is(button[name="checkout"], button.cart__checkout-button) > * {
      opacity: 0 !important;
    }
    .bw-checkout-pending :is(button[name="checkout"], button.cart__checkout-button)::after {
      content: "" !important; display: block !important; position: absolute !important;
      inset: 50% auto auto 50% !important; width: 22px !important; height: 22px !important;
      box-sizing: border-box !important; margin: -11px 0 0 -11px !important; padding: 0 !important;
      border: 2px solid currentColor !important; border-right-color: transparent !important;
      border-radius: 50% !important; background: none !important; box-shadow: none !important;
      opacity: 1 !important; pointer-events: none !important;
      animation: bw-checkout-spin .7s linear infinite !important;
    }
    @keyframes bw-checkout-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) {
      .bw-checkout-pending :is(button[name="checkout"], button.cart__checkout-button)::after {
        animation: none !important;
      }
    }
  `;
  document.body.append(loadingStyle);
  const key = 'botaleo-whop-cart-v1';
  const french = config.locale?.startsWith('fr');
  const messages = french ? {
    EMPTY: 'Votre panier est vide.',
    UNSUPPORTED: 'Ce panier nécessite une aide pour le paiement. Contactez-nous avant de commander.',
    CHANGED: 'Votre panier a changé. Vérifiez-le puis réessayez.',
    UNAVAILABLE: 'Le paiement est temporairement indisponible. Réessayez dans un instant.',
    PAID: 'Votre commande est confirmée. Merci pour votre achat !',
    REVIEW: 'Votre paiement est en cours de confirmation. Ne payez pas une seconde fois.',
    BUSY: 'Votre lien de paiement se prépare. Réessayez dans quelques instants.',
  } : {
    EMPTY: 'Your cart is empty.',
    UNSUPPORTED: 'This cart needs help with payment. Please contact us before placing your order.',
    CHANGED: 'Your cart changed. Please check it and try again.',
    UNAVAILABLE: 'Payment is temporarily unavailable. Please try again shortly.',
    PAID: 'Your order is confirmed. Thank you for your purchase!',
    REVIEW: 'Your payment is being confirmed. Please do not pay a second time.',
    BUSY: 'Your payment link is being prepared. Please try again shortly.',
  };
  let busy = false;
  let revision = 0;
  let lastChange = 0;
  let memory;
  const pending = new Set();
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
  const load = () => {
    try { return JSON.parse(sessionStorage.getItem(key)) || memory; } catch { return memory; }
  };
  const save = value => {
    memory = value;
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Mémoire seule si stockage privé bloqué. */ }
  };
  const note = code => {
    let element = document.getElementById('botaleo-whop-message');
    if (!element) {
      element = document.createElement('div'); element.id = 'botaleo-whop-message';
      element.setAttribute('role', 'status'); element.setAttribute('aria-live', 'polite');
      document.body.append(element);
    }
    const text = document.createElement('span');
    text.textContent = messages[code] || messages.UNAVAILABLE;
    const close = document.createElement('button');
    close.type = 'button'; close.className = 'bw-message-close'; close.textContent = '×';
    close.setAttribute('aria-label', french ? 'Fermer le message' : 'Dismiss message');
    close.addEventListener('click', () => { element.hidden = true; });
    element.replaceChildren(text, close);
    element.hidden = false;
  };
  const setBusy = value => {
    busy = value;
    document.documentElement.classList.toggle('bw-checkout-pending', value);
    document.querySelectorAll(selector).forEach(button => button.setAttribute('aria-busy', String(value)));
  };
  const changed = event => {
    revision++; lastChange = Date.now();
    if (event.promise) {
      const promise = Promise.resolve(event.promise);
      pending.add(promise);
      promise.then(() => { pending.delete(promise); revision++; }, () => { pending.delete(promise); revision++; });
    }
  };
  document.addEventListener('quantity-selector:update', changed);
  // Import Horizon déjà présent dans ce thème. Aucun nouveau script tiers.
  const eventsReady = import('@shopify/events').then(({ StandardEvents }) => {
    for (const type of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate, StandardEvents.cartNoteUpdate]) {
      document.addEventListener(type, changed);
    }
  });
  eventsReady.catch(() => {});
  const jsonFetch = async (url, init = {}) => {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store',
      ...init, signal: AbortSignal.timeout(55000) });
    let body;
    try { body = await response.json(); } catch { throw new Error('UNAVAILABLE'); }
    if (!response.ok) {
      const codes = { CART_TOTAL_MISMATCH: 'CHANGED', STOCK_INSUFFICIENT: 'CHANGED',
        CHECKOUT_BUSY: 'BUSY', TOO_MANY_REQUESTS: 'BUSY', CHECKOUT_ALREADY_PROCESSED: 'REVIEW' };
      throw new Error(codes[body.error] || 'UNAVAILABLE');
    }
    return body;
  };
  const getCart = () => jsonFetch(`${root}cart.js`);
  const settle = async () => {
    await Promise.race([eventsReady, sleep(10000).then(() => { throw new Error('UNAVAILABLE'); })]);
    const deadline = Date.now() + 20000;
    for (;;) {
      // Horizon applique les quantités après son debounce de 300 ms.
      await sleep(Math.max(0, 350 - (Date.now() - lastChange)));
      const all = [...pending, ...(customElements.get('cart-items-component')?.pendingQuantityUpdates || [])];
      if (!all.length) return;
      if (Date.now() > deadline) throw new Error('UNAVAILABLE');
      await Promise.race([Promise.all(all), sleep(1000)]);
    }
  };
  const status = attempt => jsonFetch(`${endpoint}?order=${encodeURIComponent(attempt.orderId)}`);
  const finishPaid = async attempt => {
    await settle();
    const version = revision;
    const cart = await getCart();
    if (!attempt.cleared && version === revision && cartFingerprint(cart, config.country) === attempt.fingerprint) {
      await jsonFetch(`${root}cart/clear.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      save({ ...attempt, cleared: true });
      window.location.assign(`${root}cart?whop_confirmed=1`);
    } else {
      // Un panier modifié depuis l'achat est conservé intégralement.
      save({ ...attempt, cleared: true });
      note('PAID');
    }
  };
  const checkout = async () => {
    if (busy || !whopEnabled()) return;
    const previousMessage = document.getElementById('botaleo-whop-message');
    if (previousMessage) previousMessage.hidden = true;
    setBusy(true);
    let navigating = false;
    try {
      await settle();
      if (!whopEnabled()) return;
      const version = revision;
      const cart = await getCart();
      const payload = cartPayload(cart, config.country);
      if (revision !== version) throw new Error('CHANGED');
      const fingerprint = cartFingerprint(cart, config.country);
      let attempt = matchingAttempt(load(), fingerprint);
      if (attempt?.orderId) {
        const result = await status(attempt);
        if (result.status === 'synced') { await finishPaid(attempt); return; }
        if (result.status !== 'pending') throw new Error('REVIEW');
      }
      if (!attempt) attempt = { fingerprint, requestId: crypto.randomUUID(), createdAt: Date.now() };
      save(attempt);
      if (!whopEnabled()) return;
      const result = await jsonFetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, requestId: attempt.requestId }) });
      const target = checkoutUrl(result.checkoutUrl);
      save({ ...attempt, orderId: result.orderId });
      await settle();
      const finalCart = await getCart();
      if (revision !== version || cartFingerprint(finalCart, config.country) !== fingerprint) throw new Error('CHANGED');
      if (!whopEnabled()) return;
      window.location.assign(target);
      navigating = true;
    } catch (error) { note(error.message); }
    finally { if (!navigating) setBusy(false); }
  };
  document.addEventListener('click', event => {
    if (!whopEnabled()) return;
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target?.closest(selector)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (!target.closest(selector).disabled) void checkout();
  }, true);
  document.addEventListener('submit', event => {
    if (!whopEnabled()) return;
    if (event.submitter?.matches(selector) || event.target?.id === 'cart-form' && !event.submitter) {
      event.preventDefault(); event.stopImmediatePropagation(); void checkout();
    }
  }, true);
  window.addEventListener('pageshow', () => setBusy(false));
  const onReturn = async () => {
    const id = new URL(window.location.href).searchParams.get('whop_order');
    const attempt = load();
    if (!id || attempt?.orderId !== id || busy) return;
    setBusy(true); note('REVIEW');
    try {
      for (let i = 0; i < 6; i++) {
        const result = await status(attempt);
        if (result.status === 'synced') { await finishPaid(attempt); return; }
        if (result.status === 'needs_review') return;
        await sleep(2000);
      }
    } catch { note('REVIEW'); }
    finally { setBusy(false); }
  };
  void onReturn();
  if (new URL(window.location.href).searchParams.get('whop_confirmed') === '1') {
    // Ce message local n'est jamais utilisé pour considérer une commande comme payée.
    if (load()?.cleared) note('PAID');
  }
}

if (typeof document !== 'undefined') {
  const element = document.getElementById('botaleo-whop-config');
  if (element) { try { install(JSON.parse(element.textContent)); } catch { /* Configuration invalide : aucun changement du panier. */ } }
}
